const hasValue = value => value !== undefined && value !== null && String(value).trim() !== ''

const firstValue = (...values) => values.find(hasValue)

const decodePathSegment = value => {
    if (!hasValue(value)) return ''
    try {
        return decodeURIComponent(String(value))
    } catch (error) {
        return String(value)
    }
}

const formatPathLabel = path => {
    const clean = String(path || '').split('?')[0].replace(/\/+$/, '')
    const segment = clean.split('/').filter(Boolean).pop()
    return decodePathSegment(segment)
}

export const parseReturnPage = value => {
    if (!hasValue(value)) return null
    try {
        const url = new URL(String(value))
        return {
            url: url.toString(),
            path: `${url.pathname}${url.search}`,
            pathname: url.pathname,
        }
    } catch (error) {
        return { url: '', path: String(value), pathname: String(value) }
    }
}

export const buildCurrentPageFromParams = params => {
    const search = params instanceof URLSearchParams ? params : new URLSearchParams(params || '')
    const returnPage = parseReturnPage(search.get('returnPage') || '')
    const explicitName = firstValue(search.get('pageName'), search.get('routeName'), search.get('currentPage'))
    if (!returnPage && !explicitName) return null
    const path = returnPage?.path || ''
    return {
        name: explicitName || formatPathLabel(path) || '当前访问页面',
        path,
        url: returnPage?.url || '',
    }
}

export const getPageDisplay = (page = {}) => {
    const path = firstValue(page.pathPattern, page.path, page.url, page.pathname, '')
    const name = firstValue(
        page.routeName,
        page.name,
        page.title,
        page.pageName,
        page.routeKey,
        formatPathLabel(path),
        '未命名页面',
    )
    const description = firstValue(
        page.description,
        page.pageDescription,
        page.purpose,
        page.usage,
        page.summary,
        '',
    )
    return {
        id: firstValue(page.id, page.routeId, page.routeKey, path, name),
        name,
        path: path || '-',
        description,
        url: firstValue(page.url, page.previewUrl, ''),
    }
}

export const resolveCurrentPage = (currentPage, statusContext) => {
    const route = firstValue(
        statusContext?.currentRoute,
        statusContext?.currentPage,
        statusContext?.requestedRoute,
        statusContext?.deniedRoute,
        null,
    )
    const display = getPageDisplay({
        ...(route || {}),
        name: currentPage?.name,
        path: currentPage?.path || route?.pathPattern || route?.path,
        url: currentPage?.url,
    })
    if (!currentPage && !route) return null
    if (!hasValue(display.name) && display.path === '-') return null
    return {
        ...display,
        name: firstValue(route?.routeName, route?.name, route?.title, currentPage?.name, display.name),
        path: firstValue(currentPage?.path, route?.pathPattern, route?.path, display.path),
        description: firstValue(route?.description, route?.pageDescription, route?.purpose, currentPage?.description, display.description),
        url: firstValue(currentPage?.url, route?.url, display.url),
    }
}

const isExplicitlyDenied = route => (
    route?.hasAccess === false
    || route?.granted === false
    || route?.accessible === false
    || route?.denied === true
)

const isExplicitlyGranted = route => (
    route?.hasAccess === true
    || route?.granted === true
    || route?.accessible === true
    || route?.denied === false
)

const pickRouteList = (...lists) => lists.find(Array.isArray)

export const getMyPermissionRoutes = statusContext => {
    const dedicated = pickRouteList(
        statusContext?.pages,
        statusContext?.myPermissions,
        statusContext?.accessibleRoutes,
        statusContext?.grantedRoutes,
        statusContext?.pagePermissions,
    )
    const candidates = dedicated !== undefined
        ? dedicated
        : (Array.isArray(statusContext?.routes) ? statusContext.routes : [])
    const usingDedicatedList = dedicated !== undefined
    return candidates.filter(route => {
        if (route?.routeType && route.routeType !== 'PAGE') return false
        if (isExplicitlyDenied(route)) return false
        return usingDedicatedList && statusContext?.pages === dedicated
            ? isExplicitlyGranted(route)
            : usingDedicatedList || isExplicitlyGranted(route)
    }).map(getPageDisplay)
}

const pathSegments = value => {
    const raw = String(value || '').trim()
    if (!raw || raw === '-') return []
    try {
        if (/^https?:\/\//i.test(raw)) {
            return new URL(raw).pathname.split('/').filter(Boolean)
        }
    } catch (error) {
        return []
    }
    return raw.split('?')[0].split('/').filter(Boolean)
}

export const pathsMatch = (left, right) => {
    const leftParts = pathSegments(left)
    const rightParts = pathSegments(right)
    if (!leftParts.length && !rightParts.length) {
        const leftPath = String(left || '').split('?')[0].replace(/\/+$/, '') || '/'
        const rightPath = String(right || '').split('?')[0].replace(/\/+$/, '') || '/'
        return leftPath === '/' && rightPath === '/'
    }
    if (!leftParts.length || !rightParts.length) return false
    const [shorter, longer] = leftParts.length <= rightParts.length
        ? [leftParts, rightParts]
        : [rightParts, leftParts]
    return shorter.every((part, index) => longer[longer.length - shorter.length + index] === part)
}

const samePage = (left, right) => {
    if (!left || !right) return false
    if (pathsMatch(left.pathPattern || left.path || left.url, right.pathPattern || right.path || right.url)) return true
    const leftId = String(left.id || '').trim()
    const rightId = String(right.id || '').trim()
    return Boolean(leftId && rightId && leftId === rightId)
}

export const buildPageAccessList = (currentPage, statusContext) => {
    const statusPages = Array.isArray(statusContext?.pages) ? statusContext.pages : []
    const matchedPage = statusPages.find(page => samePage(page, currentPage))
    const requested = resolveCurrentPage(currentPage, matchedPage
        ? { ...statusContext, currentRoute: matchedPage }
        : statusContext)
    const granted = getMyPermissionRoutes(statusContext)
    const rows = []
    const requestedHasAccess = Boolean(requested && granted.some(page => samePage(page, requested)))
    if (requested) {
        rows.push({
            ...requested,
            key: `current:${requested.id}`,
            status: requestedHasAccess ? 'granted' : 'request',
            current: true,
        })
    }
    granted.filter(page => !samePage(page, requested)).forEach(page => {
        rows.push({
            ...page,
            key: `granted:${page.id}`,
            status: 'granted',
            current: false,
        })
    })
    return rows
}

export const getCurrentPageHint = (reason, currentPage) => {
    if (!currentPage) return ''
    if (reason === 'PAGE_NOT_REGISTERED') {
        return '这个页面还没纳入权限管理。请让应用负责人同步页面清单后，再重新检查。'
    }
    if (reason === 'PAGE_CATALOG_UNSYNCED') {
        return '应用页面清单还没同步完成。请让应用负责人处理后再重新检查。'
    }
    if (reason === 'PARTNER_ACCESS_NOT_CONFIGURED') {
        return '应用还没对其他伙伴开放。请让应用负责人先完成访问配置。'
    }
    return '你还没有这个页面的访问权限。提交申请后，应用负责人会收到钉钉提醒。'
}

export const buildAccessRequestText = ({
    projectName,
    ownerUserNick,
    pageName,
    pagePath,
} = {}) => {
    const owner = ownerUserNick ? `负责人：${ownerUserNick}。` : ''
    const page = pageName || pagePath
        ? `当前访问页面：${pageName || '未命名页面'}${pagePath ? `（${pagePath}）` : ''}。`
        : '当前提示暂无页面权限。'
    return `我需要访问 Vibe 应用「${projectName || '当前应用'}」。${page}${owner}请在 AMS 应用权限设置中为我授权。`
}

export const getOwnerLandingCopy = (reason, { accessConfigured } = {}) => {
    if (!reason) return null
    if (reason === 'PARTNER_ACCESS_NOT_CONFIGURED') {
        if (accessConfigured) return null
        return {
            title: '其他人还打不开这个应用',
            description: '你是项目创建人，可以先预览页面，再设置谁能访问。',
        }
    }
    if (reason === 'PAGE_PERMISSION_NOT_GRANTED') {
        return {
            title: '有人打不开当前页面',
            description: '你可以先预览这个页面，确认后再决定是否开放给对方。',
        }
    }
    if (reason === 'PAGE_NOT_REGISTERED') {
        return {
            title: '当前页面还没纳入权限管理',
            description: '先同步页面清单。同步完成后，你可以预览，也可以设置访问范围。',
        }
    }
    if (reason === 'PAGE_CATALOG_UNSYNCED') {
        return {
            title: '应用页面还没同步完成',
            description: '先同步页面清单。同步完成后，你可以预览，也可以设置访问范围。',
        }
    }
    return null
}

export const matchRouteByPage = (routes, page) => {
    if (!page) return null
    const target = page.pathPattern || page.path || page.url
    if (!target || target === '-') return null
    return (routes || []).find(route => pathsMatch(route.pathPattern || route.path || route.url, target)) || null
}
